import { drawRider } from './drawRider';
import type { VesselDrawState } from './types';

/** Stroke cadence, rad/s. About one full left-plus-right cycle every 1.5s. */
const PADDLE_CADENCE = 4.2;

/**
 * Paddlecraft: a seated paddler in a kayak, bow right.
 *
 * The double-bladed paddle rocks so each blade dips in turn, and the paddler
 * sways into the working side while bracing against the hull's pitch.
 */
export const drawKayak = ({
  palette,
  night,
  pitchRad,
  reducedMotion,
  timeS,
}: VesselDrawState): void => {
  const { ctx } = palette;
  const c = (hex: string): string => palette.colour(hex);

  // slim hull with upturned ends
  ctx.beginPath();
  ctx.moveTo(-40, -2);
  ctx.quadraticCurveTo(-36, -5, -22, -5);
  ctx.quadraticCurveTo(8, -6, 34, -5);
  ctx.quadraticCurveTo(46, -4, 44, -1);
  ctx.quadraticCurveTo(44, 1, 40, 2);
  ctx.quadraticCurveTo(8, 4, -20, 3);
  ctx.quadraticCurveTo(-36, 3, -40, -2);
  ctx.closePath();
  const hull = ctx.createLinearGradient(0, -6, 0, 4);
  hull.addColorStop(0, c('FFB24D'));
  hull.addColorStop(0.55, c('F08A1E'));
  hull.addColorStop(1, c('C76A12'));
  ctx.fillStyle = hull;
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = c('A0560E');
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(-38, -3);
  ctx.quadraticCurveTo(2, -5, 42, -2);
  ctx.strokeStyle = c('FFD9A0');
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.globalAlpha = 1;

  ctx.fillStyle = c('7A3E0A');
  ctx.beginPath();
  ctx.ellipse(-2, -4, 9, 2.2, 0, 0, Math.PI * 2);
  ctx.fill(); // cockpit rim

  const strokePhase = reducedMotion ? 1.0 : timeS * PADDLE_CADENCE;
  const swing = Math.sin(strokePhase);

  const pivot: readonly [number, number] = [2, -12];
  const halfLength = 18;
  const angle = 0.94 * swing;
  const dx = Math.cos(angle) * halfLength;
  const dy = Math.sin(angle) * halfLength;

  const front: readonly [number, number] = [pivot[0] + dx, pivot[1] + dy];
  const back: readonly [number, number] = [pivot[0] - dx, pivot[1] - dy];
  const handA: readonly [number, number] = [
    pivot[0] + Math.cos(angle) * 6,
    pivot[1] + Math.sin(angle) * 6,
  ];
  const handB: readonly [number, number] = [
    pivot[0] - Math.cos(angle) * 6,
    pivot[1] - Math.sin(angle) * 6,
  ];

  const lean = 0.12 * swing + pitchRad * 0.38;

  drawRider(palette, -1, -5, lean, handA, handB);

  // paddle shaft + blades, over the body
  ctx.strokeStyle = c('20262C');
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(front[0], front[1]);
  ctx.lineTo(back[0], back[1]);
  ctx.stroke();

  ctx.fillStyle = c('2E6E8E');
  [front, back].forEach((blade) => {
    ctx.save();
    ctx.translate(blade[0], blade[1]);
    ctx.rotate(angle);
    ctx.beginPath();
    ctx.ellipse(0, 0, 4.6, 2.2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  });

  ctx.fillStyle = c('C8895C');
  ctx.beginPath();
  ctx.arc(handA[0], handA[1], 1.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(handB[0], handB[1], 1.6, 0, Math.PI * 2);
  ctx.fill();

  if (night > 0.04) {
    palette.navGlow(1, -26, 6, '255,250,235', Math.min(0.9, night));
  }
};
